import { styled } from "goober";
import { Button } from "./Button";
import { Dropdown } from "./Dropdown";
import { Icon, type IconName } from "./Icon";
import { InfoIcon } from "./InfoIcon";
import { PressButton } from "./PressButton";
import { PillBadge } from "./Settings/shared";
import { SpreadsheetTable, Td, Th, Thead } from "./SpreadsheetTable";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { WavyLine } from "./WavyLine";
import { fieldOutline } from "./fieldOutline";

const SEARCH_WIDTH = 320;
const COLUMN_MENU_MIN_WIDTH = 160;

const Root = styled("div")`
  display: grid;
  grid-template-columns: 176px minmax(0, 1fr);
  gap: 12px;
  width: 100%;
  height: 100%;
  min-height: 0;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto minmax(0, 1fr);
  }
`;

const Sidebar = styled("aside")`
  min-width: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;

  @media (max-width: 720px) {
    max-height: 160px;
  }
`;

const TableList = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TableButton = styled(PressButton)`
  border: 0;
  border-radius: 8px !important;
  background: transparent;
  color: var(--text-primary);
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 8px;
  text-align: left;
  cursor: pointer;

  &[data-active="true"] {
    background: var(--alpha4);
  }

  &:hover,
  &:focus-visible {
    background: var(--alpha4);
  }

  &:focus {
    outline: none;
  }
`;

const Main = styled("section")`
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--surface-primary);
  border-radius: 16px;
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  overflow: hidden;
`;

const Toolbar = styled("div")`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 56px;
  padding: 0 12px 0 24px;
  border-bottom: 0.5px solid var(--border-alpha);

  @media (max-width: 720px) {
    flex-wrap: wrap;
    padding: 8px 16px;
  }
`;

const Title = styled("div")`
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
`;

const HeaderLabel = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const SearchRoot = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  width: ${SEARCH_WIDTH}px;
  height: 32px;
  padding: 0 10px;
  background: var(--surface-primary);
  ${fieldOutline("&:focus-within")}
  border-radius: 8px;
  cursor: text;
`;

const SearchInput = styled("input")`
  all: unset;
  flex: 1;
  min-width: 0;
  font-family: InterVariable;
  font-size: 12px;
  line-height: 16px;
  color: var(--text-primary);

  &::placeholder {
    opacity: 0.3;
  }

  &:focus::placeholder {
    opacity: 0.6;
  }
`;

const SearchClear = styled(PressButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: none;
  color: var(--text-primary);
  opacity: 0.3;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
`;

const ResultViewport = styled("div")`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const HeaderCell = styled(Th)`
  max-width: 320px;
`;

const Cell = styled(Td)`
  max-width: 320px;
`;

const Footer = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 40px;
  padding: 8px 12px;
  border-top: 0.5px solid var(--border-alpha);
`;

const Pager = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Center = styled("div")`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: var(--text-secondary);
`;

export function TableLoading() {
  return (
    <Center>
      <WavyLine size={24} opacity={0.4} />
    </Center>
  );
}

export function TableStatus({ children }: { children: React.ReactNode }) {
  return (
    <Center>
      <Text variant="text-small" color="var(--text-secondary)">
        {children}
      </Text>
    </Center>
  );
}

const ALL_COLUMNS_LABEL = "All columns";

function TableSearch({
  columns,
  column,
  onColumnChange,
  search,
  onSearchChange,
}: {
  columns: string[];
  column: string | null;
  onColumnChange: (name: string | null) => void;
  search: string;
  onSearchChange: (value: string) => void;
}) {
  const check = (selected: boolean) =>
    selected ? (
      <Icon name="check" size={12} color="var(--text-secondary)" />
    ) : undefined;

  const sections =
    columns.length === 0
      ? [
          {
            items: [
              {
                type: "button" as const,
                label: "No columns",
                left: undefined,
                disabled: true,
                onClick: () => {},
              },
            ],
          },
        ]
      : [
          {
            items: [
              {
                type: "button" as const,
                key: "__all__",
                left: undefined,
                label: ALL_COLUMNS_LABEL,
                selected: column === null,
                right: check(column === null),
                onClick: () => onColumnChange(null),
              },
              ...columns.map((name) => ({
                type: "button" as const,
                key: name,
                left: undefined,
                label: name,
                selected: name === column,
                right: check(name === column),
                onClick: () => onColumnChange(name),
              })),
            ],
          },
        ];

  return (
    <SearchRoot>
      <Dropdown
        align="bottom-left"
        minWidth={COLUMN_MENU_MIN_WIDTH}
        maxWidth={SEARCH_WIDTH}
        opener={
          <PillBadge icon="search" interactive>
            {column ?? ALL_COLUMNS_LABEL}
          </PillBadge>
        }
        sections={sections}
      />
      <SearchInput
        placeholder="Search…"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
      />
      {search && (
        <SearchClear onPress={() => onSearchChange("")}>
          <Icon name="x" size={12} />
        </SearchClear>
      )}
    </SearchRoot>
  );
}

export type TablePreviewTable = { name: string; icon: IconName };
export type TablePreviewColumn = { name: string; tooltip?: React.ReactNode };
export type TablePreviewRow = {
  key: string;
  values: Record<string, React.ReactNode>;
};

export function TablePreview({
  tables,
  activeTable,
  onSelectTable,
  searchColumns,
  searchColumn,
  onSearchColumn,
  search,
  onSearch,
  columns,
  rows,
  status,
  page,
  hasPrevious,
  hasNext,
  onPage,
}: {
  tables: TablePreviewTable[];
  activeTable: string;
  onSelectTable: (name: string) => void;
  searchColumns: string[];
  searchColumn: string | null;
  onSearchColumn: (name: string | null) => void;
  search: string;
  onSearch: (value: string) => void;
  columns: TablePreviewColumn[];
  rows: TablePreviewRow[];
  status?: React.ReactNode;
  page: number;
  hasPrevious: boolean;
  hasNext: boolean;
  onPage: (page: number) => void;
}) {
  return (
    <Root>
      <Sidebar>
        <TableList>
          {tables.map((table) => (
            <TableButton
              key={table.name}
              data-active={table.name === activeTable}
              onPress={() => onSelectTable(table.name)}
            >
              <Icon name={table.icon} size={16} />
              <Text ellipsis style={{ flex: 1, minWidth: 0 }}>
                {table.name}
              </Text>
            </TableButton>
          ))}
        </TableList>
      </Sidebar>
      <Main>
        <Toolbar>
          <Title>
            <Text variant="text-regular-md" ellipsis>
              {activeTable}
            </Text>
          </Title>
          <TableSearch
            columns={searchColumns}
            column={searchColumn}
            onColumnChange={onSearchColumn}
            search={search}
            onSearchChange={onSearch}
          />
        </Toolbar>
        <ResultViewport>
          {status ?? (
            <SpreadsheetTable>
              <Thead>
                <tr>
                  {columns.map((column) => (
                    <HeaderCell key={column.name}>
                      <HeaderLabel>
                        <Text
                          variant="text-small"
                          color="var(--text-secondary)"
                          ellipsis
                        >
                          {column.name}
                        </Text>
                        {column.tooltip != null && (
                          <Tooltip content={column.tooltip}>
                            <InfoIcon />
                          </Tooltip>
                        )}
                      </HeaderLabel>
                    </HeaderCell>
                  ))}
                </tr>
              </Thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key}>
                    {columns.map((column) => (
                      <Cell key={column.name}>
                        <Text variant="text-small">
                          {row.values[column.name]}
                        </Text>
                      </Cell>
                    ))}
                  </tr>
                ))}
              </tbody>
            </SpreadsheetTable>
          )}
        </ResultViewport>
        <Footer>
          <Text variant="text-small" color="var(--text-secondary)">
            Page {page + 1}
          </Text>
          <Pager>
            <Button
              type="button"
              variant="secondary"
              icon="chevron-left"
              disabled={!hasPrevious}
              onClick={() => onPage(Math.max(0, page - 1))}
            />
            <Button
              type="button"
              variant="secondary"
              icon="chevron-right"
              disabled={!hasNext}
              onClick={() => onPage(page + 1)}
            />
          </Pager>
        </Footer>
      </Main>
    </Root>
  );
}
